import { useCallback, useEffect, useRef, useState } from 'react';
import { MARKET_READ_TIMEOUT_MS } from '~/features/market/requestPolicy';
import { extractBackendErrorDetail } from '~/features/utils';
import { withRequestDeadline } from '~/utils/requestDeadline';

const errorMessage = (err: unknown, fallback: string) => {
  const detail =
    err != null && typeof err === 'object' && 'response' in err
      ? extractBackendErrorDetail(
          (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
        )
      : undefined;
  return detail ?? (err instanceof Error ? err.message : fallback);
};

/**
 * Reads a response's negotiation rounds the first time its history is opened.
 * Closing the history cancels a read in flight, reopening retries it, and a
 * late result for another response (or an older read) is dropped.
 */
export const useNegotiationChain = <TRow>(
  responseId: number,
  load: (responseId: number, signal: AbortSignal) => Promise<TRow[]>
) => {
  const [expanded, setExpanded] = useState(false);
  const [rows, setRows] = useState<TRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latestResponseIdRef = useRef(responseId);
  const readRef = useRef<AbortController | null>(null);
  const shownResponseIdRef = useRef(responseId);
  latestResponseIdRef.current = responseId;

  useEffect(() => {
    // Reset only when the response changes. On mount the state is already
    // fresh, and resetting would undo a toggle made before this effect ran.
    if (shownResponseIdRef.current !== responseId) {
      shownResponseIdRef.current = responseId;
      readRef.current?.abort();
      setExpanded(false);
      setRows(null);
      setLoading(false);
      setError(null);
    }
    return () => readRef.current?.abort();
  }, [responseId]);

  const toggle = useCallback(async () => {
    if (expanded) {
      readRef.current?.abort();
      setExpanded(false);
      setLoading(false);
      return;
    }
    setExpanded(true);
    if (rows !== null) return;
    const requestedResponseId = responseId;
    readRef.current?.abort();
    const controller = new AbortController();
    readRef.current = controller;
    const isCurrent = () =>
      !controller.signal.aborted &&
      readRef.current === controller &&
      latestResponseIdRef.current === requestedResponseId;
    setLoading(true);
    setError(null);
    try {
      const result = await withRequestDeadline(
        (signal) => load(requestedResponseId, signal),
        MARKET_READ_TIMEOUT_MS,
        controller.signal
      );
      if (!isCurrent()) return;
      setRows(result);
    } catch (err) {
      if (!isCurrent()) return;
      setError(errorMessage(err, 'Failed to load negotiation history.'));
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [expanded, rows, responseId, load]);

  return { expanded, rows, loading, error, toggle: () => void toggle() };
};
